import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Admin CRUD schemas for hero-section inclusions - the icon + label strip shown
 * in the website hero (e.g. "Flights", "Hotels", "Meals"). Standalone master
 * with an active/inactive toggle and a manual sort order.
 */

export const HeroInclusionListItemSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  iconImageId: UuidSchema.nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type HeroInclusionListItem = z.infer<typeof HeroInclusionListItemSchema>;

export const HeroInclusionDetailSchema = HeroInclusionListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type HeroInclusionDetail = z.infer<typeof HeroInclusionDetailSchema>;

export const HeroInclusionListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type HeroInclusionListQuery = z.infer<typeof HeroInclusionListQuerySchema>;

export const HeroInclusionListResponseSchema = paginatedResponseSchema(HeroInclusionListItemSchema);
export type HeroInclusionListResponse = z.infer<typeof HeroInclusionListResponseSchema>;

const HeroInclusionCoreFieldsSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  iconImageId: UuidSchema.nullish(),
  // Optional (not .default) to keep the Zod input/output types identical, which
  // react-hook-form's resolver typing requires. The API service applies the
  // effective defaults (sortOrder → 0, isActive → true).
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateHeroInclusionRequestSchema = HeroInclusionCoreFieldsSchema;
export type CreateHeroInclusionRequest = z.infer<typeof CreateHeroInclusionRequestSchema>;

export const UpdateHeroInclusionRequestSchema = HeroInclusionCoreFieldsSchema.partial();
export type UpdateHeroInclusionRequest = z.infer<typeof UpdateHeroInclusionRequestSchema>;

/**
 * Public (website-facing) view of one master row, served by
 * GET /website/hero-inclusions: active rows in sort order, icon media id
 * already resolved to a URL. Sections that reference the master by id (the
 * landing-page cards) render their ticks from this.
 */
export const PublicHeroInclusionOptionSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  iconUrl: z.string().nullable(),
});
export type PublicHeroInclusionOption = z.infer<typeof PublicHeroInclusionOptionSchema>;
